import React from 'react';
import millify from 'millify';
import { useRecommendedChannels } from '@components/Channels/useRecommendedChannels';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import blurImage from '@components/main-components/image/blur-image';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { SPACE } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

const PROFILE_IMAGE_SIZE = 29;

function ChannelSkeleton() {
  return (
    <div className="mt-2 flex w-full items-center gap-2">
      <div className="h-7 w-7 flex-shrink-0 flex-grow-0 animate-pulse rounded-md bg-neutral200" />

      <div className="flex w-full flex-col gap-[2px]">
        <div className="h-3 w-2/3 animate-pulse rounded-md bg-neutral200" />
        <div className="h-3 w-1/2 animate-pulse rounded-md bg-neutral200" />
      </div>
    </div>
  );
}

export function ChannelsSection() {
  const { channels, isEmpty, isLoading } = useRecommendedChannels();

  if (isEmpty) {
    return null;
  }

  return (
    <div className="w-full">
      <span className="mb-4 overflow-hidden text-ellipsis whitespace-nowrap font-base text-[12px] uppercase leading-[14px] text-base500">
        Recommended Channels
      </span>

      {isLoading && (
        <>
          <ChannelSkeleton />
          <ChannelSkeleton />
          <ChannelSkeleton />
        </>
      )}

      <div className="mt-3 flex flex-col gap-4">
        {channels.map(({ channel, memberCount, hasNewContent }) => {
          const profilePictureSrc =
            channel.staticProfileImage?.url ??
            channel.profileImage?.url ??
            '/images/sound_letter_s.svg';
          const channelWebappUri = webappUriPath(channel.webappUri);

          return (
            <AccessibleLink
              key={channel.id}
              className="w-full !pl-0"
              pathnameUri={channelWebappUri}
            >
              <div className="flex w-full items-center gap-2 transition-colors duration-500 hover:brightness-[80%]">
                <div className="relative h-7 w-7 flex-shrink-0 flex-grow-0 rounded-md">
                  <NextFutureImage
                    alt="Channel Profile Image"
                    src={profilePictureSrc}
                    width={PROFILE_IMAGE_SIZE}
                    height={PROFILE_IMAGE_SIZE}
                    placeholder="blur"
                    blurDataURL={blurImage(PROFILE_IMAGE_SIZE, PROFILE_IMAGE_SIZE)}
                    className="rounded-md object-cover"
                  />
                  {!hasNewContent && (
                    <div className="absolute -right-[4px] -top-[4px] inline-block h-[10px] w-[10px] rounded-full border-[2px] border-solid border-white bg-fuchsia500 align-super" />
                  )}
                </div>

                <div className="flex w-full flex-col gap-[2px]">
                  <span className="line-clamp-1 font-base text-base-s font-semibold text-base600">
                    {channel.name}
                  </span>
                  <span className="line-clamp-1 font-base text-[11px] leading-[100%] text-base400">
                    {millify(memberCount)}
                    {SPACE}
                    {getSingularOrPlural({ count: memberCount, word: 'member' })}
                  </span>
                </div>
              </div>
            </AccessibleLink>
          );
        })}
      </div>
    </div>
  );
}
